import NoDataIcon from '@assets/no-data.svg?react';
import { Box, Tooltip } from '@mui/material';
import useIsNoComms from '@src/hooks/useIsNoComms';
import { isDataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { InventoryStatus, RunMode } from '@src/types/UnitItem';
import { memo, ReactElement, useMemo } from 'react';
import { DeviceDataTableRow } from '../DeviceDataTable/types';
import FormattedAlias from '../Translations/FormattedAlias';
import { MODE_POINTS } from './constants';
import { getCircleClass } from './utils/getCircleClass';
import './ModesDataStatus.scoped.scss';

const MODE_POINTS_SET = new Set(MODE_POINTS);

interface IconProps {
  isNoComms: boolean;
  circleClass: string;
}

function Icon(p: IconProps): ReactElement {
  if (p.isNoComms) {
    return (
      <div role='no-data-icon'>
        <NoDataIcon />
      </div>
    );
  }

  return <i role='status-circle' className={`status ${p.circleClass}`} />;
}

interface TooltipContentProps {
  invStatus: InventoryStatus | undefined;
  runMode: RunMode | undefined;
  allowedMinP: number;
  allowedMaxP: number;
  allowedMinQ: number;
  allowedMaxQ: number;
  pcsP: number;
  pcsQ: number;
}

function TooltipContent(p: TooltipContentProps): ReactElement {
  return (
    <div className='loto-mode-tooltip'>
      <div className='flex-row-s'>
        <FormattedAlias id={'Mode'} />: {p.invStatus} / {p.runMode}
      </div>
      <div className='flex-row-s'>
        <FormattedAlias id={'P-limit'} />
        &nbsp;
        {p.allowedMinP} / {p.allowedMaxP}
      </div>
      <div className='flex-row-s'>
        <FormattedAlias id={'Q-Limit'} />
        &nbsp;
        {p.allowedMinQ} / {p.allowedMaxQ}
      </div>
      <div className='flex-row-s'>
        <FormattedAlias id={'kW Cmd'} />
        &nbsp;
        {p.pcsP}
      </div>
      <div className='flex-row-s'>
        <FormattedAlias id={'kVAR Cmd'} />
        &nbsp;
        {p.pcsQ}
      </div>
    </div>
  );
}

interface ModeValues {
  invStatus: InventoryStatus | undefined;
  runMode: RunMode | undefined;
  allowedMinP: number;
  allowedMaxP: number;
  allowedMinQ: number;
  allowedMaxQ: number;
  pcsP: number;
  pcsQ: number;
}

const DEFAULT_MODE_VALUES: ModeValues = {
  invStatus: undefined,
  runMode: undefined,
  allowedMinP: 0,
  allowedMaxP: 0,
  allowedMinQ: 0,
  allowedMaxQ: 0,
  pcsP: 0,
  pcsQ: 0,
};

function extractModeValues(row: DeviceDataTableRow): ModeValues {
  const result = { ...DEFAULT_MODE_VALUES };

  for (const point of Object.values(row)) {
    if (!isDataPointWithValue(point) || !MODE_POINTS_SET.has(point.pointName)) {
      continue;
    }
    if (point.value === undefined) {
      continue;
    }

    switch (point.pointName) {
      case 'InvStatus':
        result.invStatus = point.value as InventoryStatus;
        break;
      case 'RunMode':
        result.runMode = point.value as RunMode;
        break;
      case 'AllowedMinP':
        result.allowedMinP = point.value as number;
        break;
      case 'AllowedMaxP':
        result.allowedMaxP = point.value as number;
        break;
      case 'AllowedMinQ':
        result.allowedMinQ = point.value as number;
        break;
      case 'AllowedMaxQ':
        result.allowedMaxQ = point.value as number;
        break;
      case 'PCS_P':
        result.pcsP = point.value as number;
        break;
      case 'PCS_Q':
        result.pcsQ = point.value as number;
        break;
    }
  }

  return result;
}

export interface ModesDataCellProps {
  unitSN: string;
  siteMeta: SiteMeta;
  row: DeviceDataTableRow;
}

const ModesDataCellComponent: React.FC<ModesDataCellProps> = (p) => {
  const { dataRateTimeoutSeconds } = p.siteMeta.ui;
  const { siteId } = p.siteMeta;
  const { unitSN, row } = p;

  const modeValues = useMemo(() => extractModeValues(row), [row]);

  const {
    invStatus,
    runMode,
    allowedMinP,
    allowedMaxP,
    allowedMinQ,
    allowedMaxQ,
    pcsP,
    pcsQ,
  } = modeValues;

  const circleClass = useMemo(
    () =>
      getCircleClass(invStatus, runMode, allowedMaxP, allowedMinP, p.siteMeta),
    [invStatus, runMode, allowedMaxP, allowedMinP, p.siteMeta],
  );

  const isNoComms = useIsNoComms(siteId, dataRateTimeoutSeconds, unitSN);

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      height={'100%'}
    >
      <Tooltip
        title={
          <TooltipContent
            invStatus={invStatus}
            runMode={runMode}
            allowedMinP={allowedMinP}
            allowedMaxP={allowedMaxP}
            allowedMinQ={allowedMinQ}
            allowedMaxQ={allowedMaxQ}
            pcsP={pcsP}
            pcsQ={pcsQ}
          />
        }
      >
        <Box>
          <Icon isNoComms={isNoComms} circleClass={circleClass} />
        </Box>
      </Tooltip>
    </Box>
  );
};

const ModesDataCell = memo(ModesDataCellComponent);
ModesDataCell.displayName = 'ModesDataCell';

export default ModesDataCell;
